/**
 * Clean Duty Log Configuration - No Contaminated Types
 * 
 * Mirrors the actual structure of dutyLogConfig.ts but with clean types
 * that work with the new architecture without contamination.
 */

import {
  BaseExplorerConfig,
  BaseEntity,
  EntityServiceAdapter,
  QueryParams,
  QueryResult,
  FieldTransformer,
  DisplayFormat
} from '../../../types/baseExplorerConfig';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { DutyLogEntry } from '../../../domain/model/dutylog';
import { DataResponse, DataRequest } from '../../../types/explorer';
import { dutyLogService } from '../../../services/dutyLogService';
import { backendPage, pageHasMore } from '../../../utils/pagination';

// Add interface for user context (simplified)
interface UserContext {
  userContext: string;
  userClass: string;
  userName: string;
}

// Extend DutyLogEntry to work with BaseEntity interface
export interface DutyLogEntityEntry extends BaseEntity {
  _id: string;
  body: string;
  logDate: string;
  logClass: string;
  logUser: string;
  logContext: string;
  enhanced_body?: string;
  id?: number;
}

// Field transformers for DutyLog-specific data handling
const dutyLogFieldTransformers: Record<keyof DutyLogEntityEntry, FieldTransformer> = {
  _id: {
    transform: (value: string) => value,
    displayFormat: {
      type: 'text',
      options: {}
    }
  },
  logDate: {
    transform: (value: string) => {
      if (!value) return '';
      try {
        // en-CA produces ISO-like yyyy-mm-dd date format
        return new Date(value).toLocaleString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      } catch {
        return value;
      }
    },
    displayFormat: {
      type: 'date',
      options: {
        dateFormat: 'yyyy-MM-dd HH:mm'
      }
    }
  },
  body: {
    transform: (value: string) => value || '',
    displayFormat: {
      type: 'text',
      options: {}
    }
  },
  logClass: {
    transform: (value: string) => value || '',
    displayFormat: {
      type: 'text',
      options: {}
    }
  },
  logUser: {
    transform: (value: string) => value || '',
    displayFormat: {
      type: 'text',
      options: {}
    }
  },
  logContext: {
    transform: (value: string) => value || '',
    displayFormat: {
      type: 'text',
      options: {}
    }
  },
  enhanced_body: {
    transform: (value: string | undefined) => value || '',
    displayFormat: {
      type: 'text',
      options: {}
    }
  },
  id: {
    transform: (value: number | undefined) => value?.toString() || '',
    displayFormat: {
      type: 'number',
      options: {}
    }
  }
};

// Service adapter that wraps dutyLogService
type DataRequestOperator = 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'greaterThan' | 'lessThan' | 'inRange';

function translateOperator(op: string): DataRequestOperator {
  if (op === 'eq') return 'equals';
  if (op === 'contains' || op === 'startsWith' || op === 'endsWith') return op;
  if (op === 'gt' || op === 'gte') return 'greaterThan';
  if (op === 'lt' || op === 'lte') return 'lessThan';
  return 'equals';
}

/**
 * Fields whose quick-filter is a `dropdown` (exact-match) — see the quickFilters
 * definitions below (logClass = Region, logContext = Role). The list applies
 * `equals` to these; the export must too (#693). Keep in sync with the dropdown
 * filters in the config's `filtering.quickFilters`.
 */
export const DUTY_LOG_EXACT_MATCH_FIELDS = new Set(['logClass', 'logContext']);

export const dutyLogServiceAdapter: EntityServiceAdapter<DutyLogEntityEntry> = {
  async fetchData(params: QueryParams): Promise<QueryResult<DutyLogEntityEntry>> {
    // Convert QueryParams to DataRequest format (dutyLogService uses `pageSize`)
    const { page, limit } = backendPage(params);
    const dataRequest: DataRequest = {
      page,
      pageSize: limit,
      sort: params.sort?.map(s => ({
        field: s.field,
        direction: s.direction
      })),
      filters: params.filters?.map(f => ({
        field: f.field,
        operator: translateOperator(f.operator),
        value: f.value
      })),
      search: params.search?.term,
      crosslinkSearch: params.crosslinkSearch
    };

    const response = await dutyLogService.fetchData(dataRequest);
    const responsePage = response.page ?? page;
    const responseLimit = response.limit ?? limit;
    return {
      data: response.data.map((entry) => ({
        ...entry,
        _id: entry._id || 'unknown'
      })),
      total: response.totalCount,
      page: responsePage,
      limit: responseLimit,
      hasMore: pageHasMore(responsePage, responseLimit, response.totalCount),
    };
  },

  async createEntity(data: Omit<DutyLogEntityEntry, '_id'>): Promise<DutyLogEntityEntry> {
    const result = await dutyLogService.createEntry(data as any);
    return {
      ...result,
      _id: result._id || 'unknown'
    };
  },

  async updateEntity(id: string, data: Partial<DutyLogEntityEntry>): Promise<DutyLogEntityEntry> {
    const result = await dutyLogService.updateEntry({ _id: id, ...data });
    return {
      ...result,
      _id: result._id || 'unknown'
    };
  },

  async deleteEntity(id: string): Promise<boolean> {
    await dutyLogService.deleteEntry(id);
    return true;
  },

  buildExportParams(params: QueryParams): URLSearchParams {
    const out = new URLSearchParams();
    if (params?.search?.term) out.append('search', params.search.term);
    if (params?.sort?.[0]) {
      out.append('sortBy', params.sort[0].field);
      out.append('sortOrder', params.sort[0].direction);
    }
    if (params?.filters) {
      for (const f of params.filters) {
        if (f.value == null || f.value === '') continue;
        out.append(f.field, String(f.value));
      }
    }
    return out;
  },
};

/**
 * Create clean duty log configuration - matches existing dutyLogConfig pattern
 */
export function createDutyLogConfig(userContext: UserContext): BaseExplorerConfig<DutyLogEntityEntry> {
  // Create the configuration object
  return {
    entityName: 'DutyLog',
    complexityLevel: 'simple',
    serviceAdapter: dutyLogServiceAdapter,
    displayFields: ['logDate', 'logUser', 'logContext', 'body'],
    fieldTransformers: dutyLogFieldTransformers,
    autoRefreshIntervalMs: 30000,
    features: {
      searching: {
        enabled: true,
        searchFields: ['body', 'logUser', 'logContext', 'logClass'],
        placeholder: 'Search duty logs...',
        debounceMs: 1500
      },
      filtering: {
        enabled: true,
        advancedFilters: false,
        quickFilters: [
          {
            field: 'logClass',
            type: 'dropdown',
            label: 'Region',
            options: [
              { value: 'Deh Cho', label: 'Deh Cho' },
              { value: 'HQ', label: 'HQ' },
              { value: 'Inuvik', label: 'Inuvik' },
              { value: 'North Slave', label: 'North Slave' },
              { value: 'Sahtu', label: 'Sahtu' },
              { value: 'South Slave', label: 'South Slave' }
            ]
          },
          {
            field: 'logContext',
            type: 'dropdown',
            label: 'Role',
            options: [
              { value: 'ADMIN', label: 'ADMIN (System Admins)' },
              { value: 'RDO', label: 'RDO (Regional Duty Officer)' },
              { value: 'TDO', label: 'TDO (Territorial Duty Officer)' }
            ]
          },
          { field: 'logDate', label: 'Date', type: 'dateRange' },
          { field: 'logUser', label: 'User', type: 'textSearch' },
          { field: 'body', label: 'Content', type: 'textSearch' }
        ]
      },
      sorting: {
        enabled: true,
        multiColumn: false,
        defaultSort: [{ field: 'logDate', direction: 'desc' }]
      },
      pagination: {
        enabled: true,
        defaultPageSize: 25,
        pageSizeOptions: [10, 25, 50, 100],
        showPageInfo: true
      },
      export: {
        enabled: true,
        formats: ['csv', 'excel'],
        includeFilters: true
      },
      editing: {
        enabled: false,
        mode: 'modal',
        allowCreate: true,  // Create is still allowed
        allowUpdate: false, // Update/edit is ILLEGAL for duty logs
        allowDelete: false  // Delete is not allowed
      }
    },

    presentation: {
      title: 'Duty Logs',
      icon: faClipboardList,
      columnConfig: [
        {
          field: 'logDate',
          title: 'Date',
          width: 160,
          resizable: true,
          sortable: true,
          filterable: true,
          hidden: false
        },
        {
          field: 'logUser',
          title: 'User',
          width: 120,
          resizable: true,
          sortable: true,
          filterable: true,
          hidden: false
        },
        {
          field: 'logClass',
          title: 'Region',
          width: 100,
          resizable: true,
          sortable: true,
          filterable: true,
          hidden: false
        },
        {
          field: 'logContext',
          title: 'Role',
          width: 100,
          resizable: true,
          sortable: true,
          filterable: true,
          hidden: false
        },
        {
          field: 'body',
          title: 'Content',
          width: 400,
          resizable: true,
          sortable: false,
          filterable: true,
          hidden: false
        }
      ],
      layout: {
        minHeight: 400,
        responsive: true,
        showToolbar: true,
        showFooter: true
      }
    }
  };
}
